import './index.css';
import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/shared/useAuth';
import { ToastProvider } from '@nexvia-logistics/ui';
import App from './App';
import { env } from '@/env';
import { ErrorBoundary } from '@/components/ErrorBoundary';

Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT ?? 'development',
    enabled: !!env.VITE_SENTRY_DSN,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
    ],
    tracesSampleRate: env.VITE_SENTRY_ENVIRONMENT === 'production' ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            retry: 1,
        },
    },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AuthProvider>
                    {/*
                      * INSIDE AuthProvider on purpose (F-008). Above it, a render throw would take
                      * the session with it and drop the operator on the login screen, which reads
                      * as "I was logged out" rather than "that screen broke". Inside, the session
                      * survives and the fallback is a page they can reload or navigate away from.
                      */}
                    <ErrorBoundary>
                        {/*
                          * The one `ToastProvider` in the app (T64). It replaces the per-page
                          * `{ open, message, severity }` state that eleven screens each owned, and
                          * `useToast().show(tone, message)` is how they raise one now.
                          *
                          * INSIDE `ErrorBoundary`, not outside: a toast is a transient report about
                          * a screen, and when that screen has crashed into the fallback there is
                          * nothing left for a toast to be about. Outside, a stale message would
                          * hang over the error page.
                          *
                          * `duration={4000}` preserves the local component's default. The design
                          * system's own is 5000, and silently making every toast a second longer is
                          * not something a migration should decide.
                          */}
                        <ToastProvider duration={4000}>
                            <App />
                        </ToastProvider>
                    </ErrorBoundary>
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    </React.StrictMode>
);
